import React, { useEffect, useState } from "react";
import api from "../Service/api";
import "./EmployeeSalaryView.css";

export default function EmployeeSalaryView() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchName, setSearchName] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    fetchSalaryView();
  }, []);

  useEffect(() => {
    if (!searchName) {
      setFilteredEmployees(employees);
    } else {
      setFilteredEmployees(
        employees.filter((emp) =>
          emp.employeeName.toLowerCase().includes(searchName.toLowerCase())
        )
      );
    }
  }, [searchName, employees]);

  const fetchSalaryView = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await api.get("/api/salary/details", { params });
      if (res.data.code === 1) {
        const employeesWithCalculatedNet = calculateNetSalaryWithAdvances(
          res.data.additionalDetail || []
        );
        setEmployees(employeesWithCalculatedNet);
        setFilteredEmployees(employeesWithCalculatedNet);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to load salary view");
    } finally {
      setLoading(false);
    }
  };

  const calculateNetSalaryWithAdvances = (employeesData) => {
    return employeesData.map((emp) => ({
      ...emp,
      monthlyDetails: emp.monthlyDetails.map((month) => {
        const baseSalary = parseFloat(month.baseSalary) || 0;
        const bonus = parseFloat(month.bonus) || 0;
        const overtime = parseFloat(month.overTime) || 0;
        const deduction = parseFloat(month.deduction) || 0;
        const advanceAdjustment = parseFloat(month.advanceAdjustment) || 0;
        
        const grossSalary = baseSalary + bonus + overtime;
        const netSalaryFromBackend = parseFloat(month.netSalary) || 0;
        let finalNetSalary = netSalaryFromBackend > 0 ? netSalaryFromBackend : grossSalary - deduction - advanceAdjustment;
        
        const totalPaid = month.totalPaid || finalNetSalary;
        const totalAdvances = parseFloat(month.totalAdvanceAmount) || 0;
        
        return {
          ...month,
          netSalary: finalNetSalary,
          grossSalary: grossSalary,
          totalPaid: totalPaid,
          pendingAdvance: totalAdvances - advanceAdjustment,
          hasSalaryRecord: month.status === "PAID" || month.status === "UNPAID",
          isPaid: month.status === "PAID"
        };
      })
    }));
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "-";
    return new Date(dateTime).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Delete salary by employee ID and month
  const deleteSalary = async (employeeId, salaryMonth) => {
    if (!employeeId || !salaryMonth) {
      alert("Invalid salary record");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete salary for ${formatSalaryMonth(salaryMonth)}?`)) {
      return;
    }

    try {
      const res = await api.delete(`/api/salary/delete/${employeeId}`, {
        data: { salaryMonth }
      });
      if (res.data.code === 1) {
        alert(res.data.additionalDetail || "Salary deleted successfully");
        fetchSalaryView();
      } else {
        alert(res.data.additionalDetail || "Delete failed");
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.additionalDetail || "Failed to delete salary");
    }
  };

  // Delete advance by employee ID and paid on date/time
  const deleteAdvance = async (employeeId, paidOn) => {
    if (!employeeId || !paidOn) {
      alert("Invalid advance record");
      return;
    }

    const formattedDate = new Date(paidOn).toLocaleString();
    if (!window.confirm(`Are you sure you want to delete this advance given on ${formattedDate}?`)) {
      return;
    }

    try {
      const res = await api.delete("/api/salary/advance/delete", {
        data: { 
          employeeId: employeeId,
          paidOn: paidOn
        }
      });
      if (res.data.code === 1) {
        alert(res.data.message || "Advance deleted successfully");
        fetchSalaryView();
      } else {
        alert(res.data.message || "Delete failed");
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || "Failed to delete advance");
    }
  };

  const formatSalaryMonth = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const toggleMonthExpanded = (employeeId, monthIndex) => {
    setExpandedMonths(prev => ({
      ...prev,
      [`${employeeId}-${monthIndex}`]: !prev[`${employeeId}-${monthIndex}`]
    }));
  };

  const getStatusBadge = (status, isPaid) => {
    if (status === "PAID") {
      return <span className="status-badge paid">✅ PAID</span>;
    } else if (status === "UNPAID") {
      return <span className="status-badge unpaid">⏳ UNPAID</span>;
    } else if (isPaid) {
      return <span className="status-badge paid">✅ PAID</span>;
    }
    return <span className="status-badge pending">📝 PENDING</span>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading salary data...</p>
      </div>
    );
  }

  return (
    <div className="salary-view-container">
      <h1>💰 Employee Salary Overview</h1>

      {/* Filter Section */}
      <div className="filter-row">
        <input
          type="text"
          placeholder="Search By Employee Name..."
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className="search-input"
        />

        <label>Start Date:</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />

        <label>End Date:</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />

        <button className="filter-btn" onClick={fetchSalaryView}>
          🔍 Filter
        </button>
        <button
          className="clear-btn"
          onClick={() => {
            setStartDate("");
            setEndDate("");
            setSearchName("");
            fetchSalaryView();
          }}
        >
          🗑️ Clear
        </button>
      </div>

      {/* Summary Statistics */}
      {filteredEmployees.length > 0 && (
        <div className="summary-stats">
          <div className="stat-card">
            <span className="stat-label">Total Employees</span>
            <span className="stat-value">{filteredEmployees.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Salary Paid</span>
            <span className="stat-value">
              {formatCurrency(filteredEmployees.reduce((sum, emp) => sum + (emp.totalSalaryPaid || 0), 0))}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Advances</span>
            <span className="stat-value">
              {formatCurrency(filteredEmployees.reduce((sum, emp) => sum + (emp.totalAdvanceGiven || 0), 0))}
            </span>
          </div>
        </div>
      )}

      {filteredEmployees.length === 0 && !loading && (
        <div className="no-data">
          <p>No salary or advance records found for the selected period.</p>
        </div>
      )}

      {filteredEmployees.map((emp) => (
        <div key={emp.employeeId} className="employee-card">
          <div className="employee-header">
            <div>
              <h2>{emp.employeeName}</h2>
              <span className="designation-text">{emp.designation}</span>
            </div>
            <div className="employee-stats">
              <div className="emp-stat">
                <span>Base Salary:</span>
                <strong>{formatCurrency(emp.baseSalary)}</strong>
              </div>
              <div className="emp-stat">
                <span>Total Paid:</span>
                <strong>{formatCurrency(emp.totalSalaryPaid || 0)}</strong>
              </div>
              <span className="emp-id">ID: {emp.employeeId}</span>
            </div>
          </div>

          {/* Only render monthly details if they exist */}
          {emp.monthlyDetails && emp.monthlyDetails.length > 0 && (
            <div className="monthly-details-container">
              {emp.monthlyDetails.map((month, index) => {
                const isExpanded = expandedMonths[`${emp.employeeId}-${index}`];
                const hasAdvances = month.advances && month.advances.length > 0;
                const isPaid = month.isPaid || month.status === "PAID";
                const hasSalaryRecord = month.hasSalaryRecord;

                return (
                  <div key={`${emp.employeeId}-month-${index}`} className={`month-card ${!isPaid ? 'unpaid' : ''}`}>
                    <div className="month-header" onClick={() => toggleMonthExpanded(emp.employeeId, index)}>
                      <div className="month-info">
                        <span className="month-badge">{formatSalaryMonth(month.salaryMonth)}</span>
                        {getStatusBadge(month.status, month.isPaid)}
                        {hasAdvances && (
                          <span className="advance-indicator">
                            💰 {month.totalAdvancesCount} Advance{month.totalAdvancesCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      <div className="month-actions">
                        <div className="salary-summary">
                          {hasSalaryRecord && (
                            <>
                              <span className="gross-salary">
                                Gross: {formatCurrency(month.grossSalary)}
                              </span>
                              <span className="net-salary">
                                Net: {formatCurrency(month.netSalary)}
                              </span>
                            </>
                          )}
                          {hasAdvances && !hasSalaryRecord && (
                            <span className="advance-total">
                              Total Advances: {formatCurrency(month.totalAdvanceAmount)}
                            </span>
                          )}
                          {month.pendingAdvance > 0 && (
                            <span className="pending-advance">
                              Pending: {formatCurrency(month.pendingAdvance)}
                            </span>
                          )}
                        </div>

                        {hasSalaryRecord && (
                          <button
                            className="delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSalary(emp.employeeId, month.salaryMonth);
                            }}
                          >
                            🗑️ Delete Salary
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="month-details">
                        {hasSalaryRecord && (
                          <div className="salary-breakdown">
                            <h4>💰 Salary Breakdown</h4>
                            <div className="salary-grid">
                              <div className="salary-item">
                                <label>Base Salary</label>
                                <p className="amount">{formatCurrency(month.baseSalary)}</p>
                              </div>
                              <div className="salary-item">
                                <label>Bonus</label>
                                <p className="amount positive">+{formatCurrency(month.bonus)}</p>
                              </div>
                              <div className="salary-item">
                                <label>Overtime</label>
                                <p className="amount positive">+{formatCurrency(month.overTime)}</p>
                              </div>
                              <div className="salary-item">
                                <label>Deduction</label>
                                <p className="amount negative">-{formatCurrency(month.deduction)}</p>
                              </div>
                              <div className="salary-item">
                                <label>Advance Adjusted</label>
                                <p className="amount negative">-{formatCurrency(month.advanceAdjustment)}</p>
                              </div>
                            </div>

                            <div className="salary-calculation">
                              <div className="calc-row">
                                <span>Gross Salary:</span>
                                <span>{formatCurrency(month.grossSalary)}</span>
                              </div>
                              <div className="calc-row">
                                <span>Total Deductions:</span>
                                <span>-{formatCurrency(month.deduction + month.advanceAdjustment)}</span>
                              </div>
                              <div className="calc-row total">
                                <span>Net Salary:</span>
                                <span>{formatCurrency(month.netSalary)}</span>
                              </div>
                              {month.salaryPaidOn && (
                                <div className="calc-row">
                                  <span>Paid On:</span>
                                  <span>{formatDateTime(month.salaryPaidOn)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {!hasSalaryRecord && hasAdvances && (
                          <div className="no-salary-record">
                            <p>⚠️ No salary record found for this month</p>
                            <p>Advances were given but salary not processed yet</p>
                          </div>
                        )}

                        {hasAdvances && (
                          <div className="advance-section">
                            <h4>💵 Advance Details</h4>
                            <div className="advance-table-wrapper">
                              <table className="advance-table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Advance Amount</th>
                                    <th>Adjusted Amount</th>
                                    <th>Remaining Advance</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                   </tr>
                                </thead>
                                <tbody>
                                  {month.advances.map((adv, i) => (
                                    <tr key={`adv-${i}`}>
                                      <td>{formatDateTime(adv.advanceDate)}</td>
                                      <td className="advance-amount">{formatCurrency(adv.advanceAmount)}</td>
                                      <td>{formatCurrency(adv.adjustedAmount || 0)}</td>
                                      <td className="remaining-advance">{formatCurrency(adv.remainingAdvance)}</td>
                                      <td>
                                        <span className={`status-badge ${adv.status === 'ADJUSTED' ? 'adjusted' : 'pending'}`}>
                                          {adv.status || 'PENDING'}
                                        </span>
                                      </td>
                                      <td>
                                        <button
                                          className="delete-advance-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteAdvance(emp.employeeId, adv.advanceDate);
                                          }}
                                          title="Delete this advance"
                                        >
                                          🗑️
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            
                            <div className="advance-summary">
                              <div className="advance-summary-item">
                                <span>Total Advances Given:</span>
                                <strong>{formatCurrency(month.totalAdvanceAmount)}</strong>
                              </div>
                              <div className="advance-summary-item">
                                <span>Total Adjusted:</span>
                                <strong>{formatCurrency(month.advanceAdjustment)}</strong>
                              </div>
                              <div className="advance-summary-item">
                                <span>Pending Advance:</span>
                                <strong className="pending">{formatCurrency(month.pendingAdvance)}</strong>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}